import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const services = [
  {
    n: "S/01",
    title: "Couples Therapy",
    tagline: "For partners moving through friction, distance, or repair.",
    body: "Attachment-informed work for communication, trust after rupture, intimacy, life transitions, and the every-day static that builds up between two people.",
  },
  {
    n: "S/02",
    title: "Family Therapy",
    tagline: "For households renegotiating who they are to each other.",
    body: "Multi-generational sessions to interrupt patterns, hold hard conversations, and re-build a family culture where each member can breathe.",
  },
  {
    n: "S/03",
    title: "Individual Therapy",
    tagline: "For the private work of coming home to yourself.",
    body: "Support with anxiety, self-worth, identity, life transitions, and the quiet weight of things you haven’t had a place to set down.",
  },
  {
    n: "S/04",
    title: "Premarital Counseling",
    tagline: "For the season before ‘we do’ becomes ‘we did.’",
    body: "A structured, non-clinical space to align on finances, family, sex, conflict, and the operating agreements couples rarely say out loud — before it matters.",
  },
  {
    n: "S/05",
    title: "Grief & Trauma",
    tagline: "For losses that don’t follow a schedule.",
    body: "Gentle, paced work with bereavement, complicated grief, and trauma — using evidence-informed approaches that respect your window of tolerance.",
  },
];

const ease = [0.16, 1, 0.3, 1];

export default function Services() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="services"
      data-testid="services"
      className="relative w-full py-28 sm:py-40 bg-[#EFECE5] border-y border-[#D5D1C9]"
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10">
        <div className="flex items-baseline justify-between border-b border-[#D5D1C9] pb-6">
          <span className="font-sans-ui text-[11px] uppercase tracking-[0.3em] text-[#5C5C59]">
            (Ch. II) — What we work on
          </span>
          <span className="font-serif-display italic text-[#5C5C59]">services</span>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.9, ease }}
          className="mt-12 font-serif-display font-light tracking-tighter leading-[1] text-5xl sm:text-6xl lg:text-7xl max-w-[18ch]"
        >
          A practice for the <em className="italic">relationships</em> that make a life.
        </motion.h2>

        {/* Interactive list */}
        <div
          className="mt-16 grid grid-cols-12 gap-6"
          onMouseLeave={() => setActive(0)}
        >
          <ul className="col-span-12 lg:col-span-8">
            {services.map((s, i) => {
              const isActive = active === i;
              return (
                <li
                  key={s.n}
                  data-testid={`service-row-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  tabIndex={0}
                  className={`group relative border-b border-[#D5D1C9] py-8 sm:py-10 cursor-pointer outline-none transition-[padding,background-color] duration-500 ${
                    isActive ? "sm:pl-6 bg-[#F7F5F0]/60" : "pl-0"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-6">
                    <div className="flex items-baseline gap-6 sm:gap-10">
                      <span className="font-sans-ui text-[11px] uppercase tracking-[0.3em] text-[#5C5C59]">
                        {s.n}
                      </span>
                      <h3
                        className={`font-serif-display tracking-tight leading-[1] transition-[font-style,color] duration-400 ${
                          isActive
                            ? "italic text-[#2C4C3B] text-4xl sm:text-6xl"
                            : "text-[#1A1A18] text-3xl sm:text-5xl"
                        }`}
                      >
                        {s.title}
                      </h3>
                    </div>
                    <ArrowUpRight
                      className={`h-5 w-5 shrink-0 text-[#1A1A18] transition-transform duration-500 ${
                        isActive ? "rotate-45" : ""
                      }`}
                      strokeWidth={1.5}
                    />
                  </div>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.55, ease }}
                        className="overflow-hidden"
                      >
                        <p className="mt-5 max-w-[52ch] font-sans-ui text-[15px] leading-relaxed text-[#5C5C59]">
                          <span className="text-[#1A1A18]">{s.tagline}</span> {s.body}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>

          {/* Sticky sidebar */}
          <aside className="hidden lg:block lg:col-span-4">
            <div className="sticky top-28 border border-[#D5D1C9] bg-[#F7F5F0] p-8">
              <div className="flex items-center justify-between border-b border-[#D5D1C9] pb-4">
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#5C5C59]">
                  Now viewing
                </span>
                <span className="font-serif-display italic text-[#2C4C3B]">
                  {services[active].n}
                </span>
              </div>
              <h4 className="mt-6 font-serif-display text-4xl leading-none tracking-tight text-[#1A1A18]">
                {services[active].title}
              </h4>
              <p className="mt-5 font-sans-ui text-[14px] leading-relaxed text-[#5C5C59]">
                {services[active].body}
              </p>
              <a
                href="#book"
                data-testid="services-cta"
                className="mt-8 inline-flex items-center gap-2 font-sans-ui text-[11px] uppercase tracking-[0.28em] text-[#1A1A18] link-under"
              >
                Ask about this →
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
